/**
 * Microsoft 365 (Entra ID) single sign-on, as the browser sees it.
 *
 * The feature is dark behind SSO_ENABLED on the server. While it is off every
 * /auth/sso/* route answers 404 and the sign-in screen must be exactly what it
 * was, so "enabled" is only ever a positive, well-formed 200: a 404, a network
 * error, a mocked-out transport or a body that does not say `enabled: true`
 * all read as "not enabled" and nothing SSO is rendered.
 *
 * Nothing here decides who may sign in. The server does, and this module only
 * carries its answers: where to start, what to say when it refuses, and how to
 * send an administrator to Microsoft to confirm a sensitive act.
 */
import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { apiRequest } from './api';
import config from '../config';
import { safeReturnPath, safeReturnTarget } from '../config/authRoutes';

export interface SsoConfig {
  enabled: true;
  enforced: boolean;
  provider: 'microsoft';
  startPath: string;
}

export const SSO_DEFAULT_START_PATH = '/api/v1/auth/sso/start';

/** The only host a sign-out may be sent to (the Microsoft end-session endpoint). */
const MICROSOFT_LOGOUT_PREFIX = 'https://login.microsoftonline.com/';

let configPromise: Promise<SsoConfig | null> | null = null;

/** Read once per page load; every caller shares the one answer. */
export function loadSsoConfig(): Promise<SsoConfig | null> {
  if (!configPromise) {
    configPromise = (async (): Promise<SsoConfig | null> => {
      try {
        const r = await apiRequest<Partial<SsoConfig>>('GET', '/auth/sso/config');
        const d = r && r.success ? r.data : undefined;
        if (d && d.enabled === true) {
          return {
            enabled: true,
            enforced: d.enforced === true,
            provider: 'microsoft',
            startPath: safeReturnPath(d.startPath) || SSO_DEFAULT_START_PATH,
          };
        }
      } catch {
        /* unreachable server reads as disabled */
      }
      return null;
    })();
  }
  return configPromise;
}

/** Test seam: forget the cached answer so the next load asks again. */
export function resetSsoConfigCache(): void {
  configPromise = null;
}

/** The SSO config once it is known, and null while loading or when SSO is off. */
export function useSsoConfig(): SsoConfig | null {
  const [cfg, setCfg] = useState<SsoConfig | null>(null);
  useEffect(() => {
    let live = true;
    loadSsoConfig().then((c) => { if (live) setCfg(c); });
    return () => { live = false; };
  }, []);
  return cfg;
}

/**
 * Seam for leaving the app, so it is testable: jsdom cannot navigate. Mirrors
 * `authNav` in config/authRoutes.
 */
export const ssoNav = {
  go: (url: string): void => {
    window.location.assign(url);
  },
};

/**
 * A destination carried as one string ("/path?query") as a validated path, or
 * null. Goes through `safeReturnTarget`, so `//evil.example`, `/\evil`, a
 * scheme or an encoded leading slash are refused; the query survives only in
 * its plain shape. Never a bare `startsWith('/')`.
 */
export function safeReturnString(candidate: unknown): string | null {
  if (typeof candidate !== 'string' || !candidate) return null;
  const q = candidate.indexOf('?');
  const hash = candidate.indexOf('#');
  const cut = [q, hash].filter((i) => i >= 0).sort((a, b) => a - b)[0];
  const pathname = cut === undefined ? candidate : candidate.slice(0, cut);
  const search = q >= 0 && (hash < 0 || q < hash) ? candidate.slice(q, hash >= 0 ? hash : undefined) : '';
  return safeReturnTarget({ pathname, search });
}

/** Where the browser is now, as a validated path + query (null when it cannot be carried). */
export function currentReturnTarget(): string | null {
  return safeReturnTarget({ pathname: window.location.pathname, search: window.location.search });
}

/**
 * The URL that starts a Microsoft sign-in. Same origin as the page unless the
 * app's API base points elsewhere; `returnTo` is re-validated and dropped when
 * it is not a plain path.
 */
export function ssoStartUrl(startPath: string | undefined, returnTo: string | null | undefined): string {
  const path = safeReturnPath(startPath) || SSO_DEFAULT_START_PATH;
  let origin = '';
  try {
    const base = new URL(config.apiBaseUrl, window.location.origin);
    if (base.origin !== window.location.origin) origin = base.origin;
  } catch { /* relative base: same origin */ }
  const safe = safeReturnString(returnTo);
  return `${origin}${path}${safe ? `?returnTo=${encodeURIComponent(safe)}` : ''}`;
}

/** The POST that finishes a sign-in: one-time code in, the password-login body out. */
export type SsoExchangeResult =
  | { ok: true; data: any }
  | { ok: false; code: string; status?: number; message?: string };

/**
 * Trade the one-time handoff code for a session. A raw `fetch`, deliberately:
 * the browser-binding cookie must travel (`credentials: 'include'`) and no
 * Authorization header may be sent, which the shared client would add.
 */
export async function exchangeSsoCode(code: string): Promise<SsoExchangeResult> {
  let res: Response;
  try {
    res = await fetch(`${config.apiBaseUrl}/auth/sso/exchange`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ code }),
    });
  } catch {
    return { ok: false, code: 'SSO_UNAVAILABLE' };
  }
  let body: any = null;
  try { body = await res.json(); } catch { /* not JSON */ }
  if (res.ok && body && body.success && body.data) return { ok: true, data: body.data };
  return {
    ok: false,
    code: typeof body?.code === 'string' && body.code ? body.code : 'SSO_UNAVAILABLE',
    status: res.status,
    message: typeof body?.message === 'string' ? body.message : undefined,
  };
}

export type SsoStepUpPurpose = 'ADMIN_RECOVERY' | 'SEND_BACK' | 'IMPERSONATE';

export const STEP_UP_SSO_REQUIRED = 'STEP_UP_SSO_REQUIRED';

/**
 * Send a Microsoft-signed-in administrator to Microsoft to confirm a sensitive
 * act. On success the browser leaves for `authorizeUrl` and comes back to
 * /auth/microsoft, which returns it to `returnTo` (default: where it is now).
 * Resolves true when it is leaving, false after showing why it is not.
 */
export async function startSsoStepUp(purpose: SsoStepUpPurpose, returnTo?: string | null): Promise<boolean> {
  const target = returnTo === undefined ? currentReturnTarget() : safeReturnString(returnTo);
  const r = await apiRequest<{ authorizeUrl?: string }>(
    'POST', '/auth/sso/stepup', { purpose, ...(target ? { returnTo: target } : {}) },
  );
  const url = r && r.success ? r.data?.authorizeUrl : undefined;
  if (typeof url === 'string' && url.startsWith('https://')) {
    ssoNav.go(url);
    return true;
  }
  toast.error((r && r.message) || 'Could not start the Microsoft confirmation. Try again.');
  return false;
}

/** The Microsoft sign-out address from a logout response, if it is one we will follow. */
export function pickSsoLogoutUrl(response: unknown): string | null {
  const r = response as { ssoLogoutUrl?: unknown; data?: { ssoLogoutUrl?: unknown } } | null | undefined;
  const url = r?.ssoLogoutUrl ?? r?.data?.ssoLogoutUrl;
  return typeof url === 'string' && url.startsWith(MICROSOFT_LOGOUT_PREFIX) ? url : null;
}

export const SSO_REQUIRED_COPY = 'Your account signs in with Microsoft. Use "Sign in with Microsoft" above.';
export const SSO_GENERIC_ERROR = 'Microsoft sign-in did not go through. Start again.';
const SSO_UNAVAILABLE_COPY =
  'Microsoft sign-in is not working right now. Try again in a few minutes; if it keeps failing, tell Isaiah.';

/** Refusal code -> the sentence the person reads. Anything else gets the generic one. */
export const SSO_ERROR_COPY: Record<string, string> = {
  SSO_NOT_LINKED: 'Your Microsoft account is not linked to an eMerge account yet. Ask Isaiah or your manager.',
  SSO_ACCOUNT_INACTIVE: 'Your eMerge account is not active. Ask Isaiah or your manager.',
  SSO_NOT_WORKFORCE: 'This eMerge account does not sign in with Microsoft. Use the other sign-in options.',
  SSO_LINK_CONFLICT: 'This Microsoft account is linked to a different eMerge account. Ask Isaiah to check it.',
  SSO_MFA_REQUIRED: 'Microsoft did not confirm a second factor (MFA) for this sign-in. Sign in again and complete the Microsoft verification step.',
  SSO_STATE_INVALID: 'That sign-in link expired or was already used. Start again.',
  SSO_BROWSER_MISMATCH: 'Sign-in has to finish in the same browser it started in. Start again here.',
  SSO_HANDOFF_INVALID: 'That sign-in expired before it finished. Start again.',
  SSO_PROVIDER_ERROR: 'Microsoft did not complete the sign-in (it may have been cancelled). Start again.',
  SSO_TOKEN_INVALID: SSO_UNAVAILABLE_COPY,
  SSO_TOKEN_EXCHANGE_FAILED: SSO_UNAVAILABLE_COPY,
  SSO_UNAVAILABLE: SSO_UNAVAILABLE_COPY,
  SSO_STEPUP_STALE: 'The confirmation has to be a fresh Microsoft sign-in. Try again.',
  SSO_STEPUP_WRONG_ACCOUNT: 'You confirmed with a different Microsoft account from the one linked to your eMerge account. Try again with your own account.',
};

export const ssoErrorCopy = (code: string | null | undefined): string =>
  (code && Object.prototype.hasOwnProperty.call(SSO_ERROR_COPY, code) ? SSO_ERROR_COPY[code] : SSO_GENERIC_ERROR);
